import { difference, get } from 'lodash';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import VisibilityTracker from '@swf/components/VisibilityTracker/VisibilityTracker';
import StarsWidgetContext from '@swf/core/providers/StarsWidgetContext';
import TrackingContext from '@swf/core/providers/TrackingContext';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type {
  FetchServiceMeta,
  WidgetContainer,
  WidgetInstanceProps
} from '@swf/core/types/Widget';
import widgets from '@swf/widgets/index';

import useClearWidget from './hooks/useClearWidget';
import useFetchService from './hooks/useFetchService';
import useTrackingContext from './hooks/useTrackingContext';
import useUpdateWidgetState from './hooks/useUpdateWidgetState';

const StarsWidget: React.FC<WidgetContainer> = (props) => {
  const { config, id, layout, type, meta, services = {}, preload } = props;
  const Widget = widgets[type] as React.FC<WidgetInstanceProps>;

  const data = useSelector((rootState: RootState) => select.widgets.getData(rootState, id));
  const clear = useClearWidget(id);
  const updateWidgetState = useUpdateWidgetState(props);
  const [trackingContext, updateTrackingContext] = useTrackingContext(props);
  const [subscriptions, unsubscribe, fetchService] = useFetchService(props, updateTrackingContext);

  const updateSubscriptions = useCallback(
    async (service: string, metas: FetchServiceMeta[] = []) => {
      if (get(props, ['services', service, 'config', 'subscribe'])) {
        const oldSubs = Object.keys(subscriptions[service] || {});
        const newSubs = await Promise.all(metas.map((meta) => fetchService(service, {}, meta)));
        difference(oldSubs, newSubs).forEach((destination) => unsubscribe(destination));
      }
    },
    [fetchService, props, subscriptions, unsubscribe]
  );

  return (
    <StarsWidgetContext.Provider value={{ widgetId: id, updateWidgetState }}>
      <TrackingContext.Provider value={trackingContext}>
        <VisibilityTracker name={meta.name}>
          <Widget
            clear={clear}
            config={config}
            data={data}
            fetchService={fetchService}
            id={id}
            preload={preload}
            services={services}
            style={layout}
            unsubscribe={unsubscribe}
            updateState={updateWidgetState}
            updateSubscriptions={updateSubscriptions}
          />
        </VisibilityTracker>
      </TrackingContext.Provider>
    </StarsWidgetContext.Provider>
  );
};

export default StarsWidget;
